import { AbstractControl, Validators } from '@angular/forms';
import {
    STRING_LOWERCASE_REGEXP,
    STRING_NUMBER_REGEXP,
    STRING_SPECIALCHAR_REGEXP,
    STRING_UPPERCASE_REGEXP,
    VALID_ADDRESS_REGEX,
    VALID_COMPANY_NAME_REGEX,
    VALID_LABSYS_MOBILE_PHONE_NUMBER_REGEX,
    VALID_NAME_REGEX,
    VALID_PHONE_EXTENSION,
    VALID_POSTCODE_REGEX,
    WINDOWS_1252_CHARSET,
    hasRegexPattern
} from '@app/utils/strings';
import { isValidPhoneNumber } from 'libphonenumber-js';

const isEmptyControl = (control: AbstractControl) =>
    !!control && (control.value === undefined || control.value === null || control.value === '');

export class Validations {
    public static readonly FIRST_NAME_MAX_CHARACTERS = 30;
    public static readonly LAST_NAME_MAX_CHARACTERS = 64;

    public static readonly FIRST_NAME = [
        Validators.required,
        Validators.maxLength(Validations.FIRST_NAME_MAX_CHARACTERS),
        Validators.pattern(VALID_NAME_REGEX),
        this.validateCharacters
    ];

    public static readonly LAST_NAME = [
        Validators.required,
        Validators.maxLength(Validations.LAST_NAME_MAX_CHARACTERS),
        Validators.pattern(VALID_NAME_REGEX),
        this.validateCharacters
    ];

    public static readonly EMAIL = [
        Validators.required,
        Validators.maxLength(60),
        Validators.email,
        this.validateCharacters
    ];

    public static readonly COMPANY = [
        Validators.maxLength(180),
        Validators.pattern(VALID_COMPANY_NAME_REGEX),
        this.validateCharacters
    ];

    public static readonly PHONE_EXTENSION = [
        Validators.maxLength(6),
        Validators.pattern(VALID_PHONE_EXTENSION)
    ];

    public static readonly PASSWORD = [
        Validators.required,
        Validators.minLength(8),
        this.doesPasswordPassPolicy,
    ];

    public static readonly ADDRESS_RECIPIENT_INFO = [
        Validators.maxLength(50),
        Validators.pattern(VALID_ADDRESS_REGEX),
        this.validateCharacters
    ];

    public static readonly ADDRESS_LINE_1 = [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(VALID_ADDRESS_REGEX),
        this.validateCharacters
    ];

    public static readonly ADDRESS_LINE_2 = [
        Validators.maxLength(50),
        Validators.pattern(VALID_ADDRESS_REGEX),
        this.validateCharacters
    ];

    public static readonly ADDRESS_PROVINCE = [
        Validators.maxLength(50),
        Validators.pattern(VALID_ADDRESS_REGEX),
        this.validateCharacters
    ];

    public static readonly ADDRESS_TOWN_OR_CITY = [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(VALID_ADDRESS_REGEX),
        this.validateCharacters
    ];

    public static readonly ADDRESS_COUNTRY = [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(VALID_ADDRESS_REGEX),
        this.validateCharacters
    ];

    public static readonly ADDRESS_POST_CODE = [
        Validators.required,
        Validators.maxLength(16),
        Validators.pattern(VALID_POSTCODE_REGEX)
    ];

    public static readonly ADDRESS_NZPOST_DPID = [
        Validators.required,
        Validators.maxLength(10)
    ];

    public static readonly TERMS_AND_CONDITIONS = [
        Validators.requiredTrue
    ];

    public static readonly HOME_PHONE = [
        this.validNzPhoneNumber
    ];

    public static readonly MOBILE_PHONE = [
        this.validNzPhoneNumber,
        Validators.pattern(VALID_LABSYS_MOBILE_PHONE_NUMBER_REGEX)
    ];

    public static readonly CONFIRM_PASSWORD = (passwordFormControl) => [
        Validators.required,
        (formControl) =>  this.mustMatchFieldValidator(formControl, passwordFormControl),
    ];

    private static validNzPhoneNumber(formControl: AbstractControl) {
        const formGroup = formControl.parent;

        if (!formGroup) {
            // not initialized
            return null;
        }

        if (formControl.value && !isValidPhoneNumber(formControl.value, 'NZ')) {
            return { pattern: true };
        }

        return null;
    }

    private static validateCharacters(formControl: AbstractControl) {
        const formGroup = formControl.parent;

        if (!formGroup) {
            // not initialized
            return null;
        }

        if (!WINDOWS_1252_CHARSET.test(formControl.value)) {
            return { invalidCharacter: true };
        }

        return null;
    }

    private static atLeastOneValuePresentValidator(formControl: AbstractControl, otherControlNames: string[]) {
        const formGroup = formControl.parent;

        if (!formGroup) {
            // not initialized
            return null;
        }

        const controlsToCheck = [formControl, ...otherControlNames.map(name => formGroup.get(name))];

        for (const control of controlsToCheck) {
            if (!isEmptyControl(control)) {
                return null;
            }
        }

        return { missingContactNumber: true };
    }

    private static mustMatchFieldValidator(formControl: AbstractControl, otherControlName: string) {
        const formGroup = formControl.parent;

        if (!formGroup) {
            // not initialized
            return null;
        }

        const controlToMatch = formGroup.get(otherControlName);

        if (controlToMatch && formControl.value !== controlToMatch.value) {
            return { doesNotMatch: true };
        }

        return null;
    }

    private static doesPasswordPassPolicy(formControl: AbstractControl) {
        const formGroup = formControl.parent;
        if (!formGroup) {
            return null;
        }
        const passwordControl = formControl;
        let lowerCasePattern = false;
        let upperCasePattern = false;
        let numericPattern = false;
        let specialCharPattern = false;
        let counter = 0;
        if (hasRegexPattern(passwordControl.value, STRING_LOWERCASE_REGEXP)) {
            counter++;
            lowerCasePattern = true;
        }
        if (hasRegexPattern(passwordControl.value, STRING_UPPERCASE_REGEXP)) {
            counter++;
            upperCasePattern = true;
        }
        if (hasRegexPattern(passwordControl.value, STRING_NUMBER_REGEXP)) {
            counter++;
            numericPattern = true;
        }
        if (hasRegexPattern(passwordControl.value, STRING_SPECIALCHAR_REGEXP)) {
            counter++;
            specialCharPattern = true;
        }
        if (counter < 3) {
            return {
                passwordPolicyFail: {
                    lowerCase: lowerCasePattern,
                    upperCase: upperCasePattern,
                    numeric: numericPattern,
                    specialChar: specialCharPattern,
                    counter
                }
            };
        }
        return null;
    }
}
